
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";

export default function Index() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-slate-50 py-16 px-4 md:py-24">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900">
                Secure Your Website With Automated Backups
              </h1>
              <p className="text-xl text-gray-600">
                Never lose your important data again. BackupPro provides reliable, scheduled backups for WordPress, Shopify, and WooCommerce.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" asChild>
                  <Link to="/register">Get Started</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/features">Learn More</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-video bg-gray-100 rounded-lg shadow-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2426&q=80" 
                  alt="Backup Dashboard"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Choose BackupPro?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our automated backup solution keeps your websites safe and your mind at ease.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Scheduled Backups</h3>
              <p className="text-gray-600">
                Set up automatic backups on your preferred schedule - daily, weekly, or monthly.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Secure Storage</h3>
              <p className="text-gray-600">
                Your backups are encrypted and stored securely in the cloud for easy access when needed.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">One-Click Restore</h3>
              <p className="text-gray-600">
                Easily restore your website with a single click in case of data loss or website issues.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Platforms Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Supported Platforms</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We support all major e-commerce and content management platforms.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* WordPress */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center">
              <svg className="h-16 w-16 text-blue-600 mb-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 19.5c-5.238 0-9.5-4.262-9.5-9.5S6.762 2.5 12 2.5s9.5 4.262 9.5 9.5-4.262 9.5-9.5 9.5zm-4.25-7h2.067l.583-3.256L11.982 15h2.035l1.582-3.756L16.183 15h2.067l-1.75-9h-1.917l-1.75 4.083L11.083 6h-1.917l-1.75 9z"/>
              </svg>
              <h3 className="text-xl font-semibold mb-2">WordPress</h3>
              <p className="text-gray-600 text-center">
                Full backup support for WordPress sites including themes, plugins, and databases.
              </p>
            </div>
            
            {/* Shopify */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center">
              <svg className="h-16 w-16 text-green-600 mb-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16.656 2.617c.08.116.157.237.229.363l.99.182c.025.063.047.13.068.198h-.065c-.174-.012-.675.035-1.008.11-.235-.704-.649-1.344-1.384-1.344-.02 0-.041 0-.063.002-.016-.02-.035-.037-.051-.057-.249-.258-.442-.145-.496-.12-.678.518-.478 2.65-.254 3.554-1.146.356-1.93.603-2.025.634-1.524.503-1.57.55-1.773.924l-1.123 3.517.811.242-.188.622-3.327-.818-.977 1.982h11.246L16.001 6.69c.146-.48.139-.926-.063-1.326-.127-.253-.512-.452-1.04-.545.078-.338.226-.97.31-1.24.242-.008.875-.053 1.09-.2l.357-.762z"/>
              </svg>
              <h3 className="text-xl font-semibold mb-2">Shopify</h3>
              <p className="text-gray-600 text-center">
                Comprehensive backups for your Shopify store including products, customers, and orders.
              </p>
            </div>
            
            {/* WooCommerce */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col items-center">
              <svg className="h-16 w-16 text-purple-600 mb-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 10.785l-3.734-6.534C14.784 3.442 13.836 3 12.914 3H5.458c-.841 0-1.584.38-2.082 1.042s-.675 1.499-.478 2.318l2.48 10.27c.392 1.626 1.826 2.744 3.492 2.744h8.383c1.239 0 2.345-.77 2.792-1.938l2.155-5.577c.317-.818.245-1.722-.202-2.489v-.585zM6.375 5.625h6.188l.467.938H8.344l-1.172-1.172a.25.25 0 01.191-.391h5.25c.172 0 .25.219.125.344L9.562 8.907l.663 1.469h5.25l-1.172 1.172 1.172 2.344h-3.75l-.586-1.172h1.875l-.586-1.172H8.562L9.75 14.25h-1.5l-3-6h7.875l.586-1.172H7.875l.586-1.172H9l-.586-1.172-.188-.375a.25.25 0 01.149-.391z"/>
              </svg>
              <h3 className="text-xl font-semibold mb-2">WooCommerce</h3>
              <p className="text-gray-600 text-center">
                Specialized backups for WooCommerce stores, preserving your products, orders, and configurations.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary text-white">
        <div className="container mx-auto max-w-6xl text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to secure your website?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of website owners who trust BackupPro for their backup needs.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/register">Start Your Free Trial</Link>
          </Button>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">BackupPro</h3>
              <p className="text-sm">
                Secure, reliable website backups for peace of mind.
              </p>
            </div>
            <div>
              <h4 className="text-white text-base font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-base font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-base font-medium mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} BackupPro. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
